import React from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { getIconSVG } from '../utils/icons';

const WAYPOINT_TYPES = {
  HOME: { icon: 'home', color: '#f59e0b' },
  TAKEOFF: { icon: 'takeoff', color: '#10b981' },
  WAYPOINT: { icon: 'waypoint', color: '#3b82f6' },
  LOITER_UNLIM: { icon: 'loiter', color: '#8b5cf6' },
  LOITER_TURNS: { icon: 'loiterTurns', color: '#8b5cf6' },
  LOITER_TIME: { icon: 'loiterTime', color: '#8b5cf6' },
  LAND: { icon: 'land', color: '#ef4444' },
  RTL: { icon: 'rtl', color: '#f59e0b' },
  CAMERA_TRIGGER: { icon: 'camera', color: '#ec4899' },
  CHANGE_SPEED: { icon: 'speed', color: '#06b6d4' },
  SET_ROI: { icon: 'roi', color: '#6366f1' },
  CONDITION_DELAY: { icon: 'delay', color: '#14b8a6' },
  CONDITION_DISTANCE: { icon: 'distance', color: '#14b8a6' },
  CONDITION_YAW: { icon: 'yaw', color: '#14b8a6' },
  CONDITION_CHANGE_ALT: { icon: 'altitude', color: '#14b8a6' },
};

function DraggableMarker({ position, waypoint, index, onDragEnd, onDragStart, onEdit, onDelete, onSelect, type, isHome = false, isSelected = false }) {
  const markerRef = React.useRef(null);
  const [, setIsDragging] = React.useState(false);
  const map = useMap();

  const displayType = isHome ? 'HOME' : type;
  const wpConfig = WAYPOINT_TYPES[displayType] || WAYPOINT_TYPES.WAYPOINT;

  const eventHandlers = React.useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const { lat, lng } = marker.getLatLng();
          if (map.dragging && !map.dragging.enabled()) {
            map.dragging.enable();
          }
          onDragEnd(waypoint.id, lat, lng);
          setIsDragging(false);
        }
      },
      dragstart() {
        setIsDragging(true);
        map.closePopup();
        if (map.dragging && map.dragging.enabled()) {
          map.dragging.disable();
        }
        if (onDragStart) onDragStart(waypoint.id);
      },
      click() {
        if (onSelect) onSelect(waypoint.id);
      }
    }),
    [map, onDragEnd, onDragStart, onSelect, waypoint.id]
  );

  const icon = React.useMemo(() => L.divIcon({
    className: `waypoint-marker ${isSelected ? 'waypoint-marker-selected' : ''}`,
    html: `
      <div style="
        background: ${wpConfig.color};
        width: ${isSelected ? 42 : 36}px;
        height: ${isSelected ? 42 : 36}px;
        border-radius: 50%;
        border: 3px solid #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-weight: 600;
        font-size: 14px;
        box-shadow: ${isSelected ? '0 0 0 4px rgba(34, 211, 238, 0.25), 0 8px 20px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.3)'};
        cursor: grab;
        touch-action: none;
        transform: ${isSelected ? 'scale(1.02)' : 'scale(1)'};
      ">
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <div style="display: flex; align-items: center; justify-content: center; width: 20px; height: 20px;">${getIconSVG(wpConfig.icon, 18, '#fff')}</div>
          <span style="font-size: 10px; margin-top: -2px; color: #fff;">${isHome ? 'H' : index + 1}</span>
        </div>
      </div>
    `,
    iconSize: isSelected ? [42, 42] : [36, 36],
    iconAnchor: isSelected ? [21, 21] : [18, 18],
  }), [index, isHome, isSelected, wpConfig.color, wpConfig.icon]);

  return (
    <Marker
      draggable={true}
      autoPan={true}
      riseOnHover={true}
      zIndexOffset={isSelected ? 1000 : 0}
      eventHandlers={eventHandlers}
      position={position}
      icon={icon}
      ref={markerRef}
    >
      <Popup>
        <div style={{ minWidth: '200px' }}>
          <div style={{ fontWeight: 600, marginBottom: '8px', color: wpConfig.color }}>
            {isHome ? 'HOME' : `Waypoint ${index + 1}`}: <span dangerouslySetInnerHTML={{ __html: getIconSVG(wpConfig.icon, 14, wpConfig.color) }} style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }} /> {displayType}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginBottom: '8px' }}>
            <div>Lat: {waypoint.latitude.toFixed(6)}</div>
            <div>Lon: {waypoint.longitude.toFixed(6)}</div>
            <div>Alt: {waypoint.altitude}m</div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              style={{
                padding: '4px 8px',
                background: 'var(--accentSecondary)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                flex: 1
              }}
            >
              Edit
            </button>
            {isHome ? (
              <button
                disabled
                style={{
                  padding: '4px 8px',
                  background: 'var(--bgTertiary)',
                  color: 'var(--textMuted)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'not-allowed',
                  fontSize: '11px',
                  flex: 1
                }}
              >
                Locked
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                style={{
                  padding: '4px 8px',
                  background: 'var(--danger)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  flex: 1
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

export default React.memo(DraggableMarker, (prevProps, nextProps) => {
  return prevProps.index === nextProps.index
    && prevProps.type === nextProps.type
    && prevProps.isSelected === nextProps.isSelected
    && prevProps.position[0] === nextProps.position[0]
    && prevProps.position[1] === nextProps.position[1]
    && prevProps.waypoint.id === nextProps.waypoint.id
    && prevProps.waypoint.altitude === nextProps.waypoint.altitude
    && prevProps.waypoint.latitude === nextProps.waypoint.latitude
    && prevProps.waypoint.longitude === nextProps.waypoint.longitude;
});



